/* MuFod v2.8.116 — deferred optimistic multi-remove batching + lightweight sync */
(() => {
  const drawerSelector = '[data-mufod-cart-drawer]';
  const isEditor = () => Boolean(window.Shopify && window.Shopify.designMode);
  let lastFocused = null;
  let cartBusy = false;
  let removeIntent = null;
  let cartTransactionId = 0;
  let lastCartMutationStartedAt = 0;
  let cartChallengeUntil = 0;
  let removalBatchInFlight = false;
  let removalFlushTimer = 0;
  let removalBatchStartedAt = 0;
  const pendingRemovals = new Map();

  // HAR-confirmed 429 protection: keep real customer cart mutations from arriving
  // in a tight burst. This does not bypass Shopify/Cloudflare verification; it
  // deliberately reduces request pressure and pauses new cart calls after a
  // challenge response so the storefront does not make the verification state worse.
  const CART_MUTATION_GAP_MS = 750;
  const CART_CHALLENGE_COOLDOWN_MS = 15000;
  const CART_CHALLENGE_STORAGE_KEY = 'mufod:cart-challenge-until:v114';
  const CART_REMOVE_QUIET_MS = 1400;
  const CART_REMOVE_MAX_WAIT_MS = 3200;

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const readStoredChallengeUntil = () => {
    try {
      const value = Number(sessionStorage.getItem(CART_CHALLENGE_STORAGE_KEY) || 0);
      return Number.isFinite(value) ? value : 0;
    } catch (_) {
      return 0;
    }
  };

  cartChallengeUntil = Math.max(cartChallengeUntil, readStoredChallengeUntil());

  const cartChallengeRemainingMs = () => Math.max(0, cartChallengeUntil - Date.now());

  const cartChallengeMessage = () => {
    const seconds = Math.max(1, Math.ceil(cartChallengeRemainingMs() / 1000));
    return `Shopify is temporarily verifying the cart connection. Please wait ${seconds} seconds and try again.`;
  };

  const makeChallengeError = (response = null) => {
    const error = new Error(cartChallengeMessage());
    error.mufodTechnicalAddError = true;
    error.mufodVerificationBlocked = true;
    error.mufodCooldown = true;
    error.mufodHttpStatus = Number(response?.status || 429);
    return error;
  };

  const markCartChallenge = (response = null) => {
    let cooldownMs = CART_CHALLENGE_COOLDOWN_MS;
    const retryAfter = String(response?.headers?.get?.('retry-after') || '').trim();
    if (/^\d+$/.test(retryAfter)) {
      cooldownMs = Math.max(cooldownMs, Math.min(60000, Number(retryAfter) * 1000));
    }
    cartChallengeUntil = Math.max(cartChallengeUntil, Date.now() + cooldownMs);
    try { sessionStorage.setItem(CART_CHALLENGE_STORAGE_KEY, String(cartChallengeUntil)); } catch (_) {}
  };

  const clearCartChallengeState = () => {
    cartChallengeUntil = 0;
    try { sessionStorage.removeItem(CART_CHALLENGE_STORAGE_KEY); } catch (_) {}
  };

  const clearExpiredCartChallenge = () => {
    if (cartChallengeRemainingMs() > 0) return;
    clearCartChallengeState();
  };

  const isChallengeResponse = (response) => {
    const status = Number(response?.status || 0);
    const mitigated = String(response?.headers?.get?.('cf-mitigated') || '').toLowerCase();
    return status === 429 || status === 403 || mitigated === 'challenge';
  };

  const waitForCartMutationWindow = async ({allowDuringChallenge = false} = {}) => {
    clearExpiredCartChallenge();
    if (!allowDuringChallenge && cartChallengeRemainingMs() > 0) throw makeChallengeError();

    const elapsed = Date.now() - lastCartMutationStartedAt;
    const waitMs = lastCartMutationStartedAt > 0 ? Math.max(0, CART_MUTATION_GAP_MS - elapsed) : 0;
    if (waitMs > 0) await sleep(waitMs);

    clearExpiredCartChallenge();
    if (!allowDuringChallenge && cartChallengeRemainingMs() > 0) throw makeChallengeError();
    lastCartMutationStartedAt = Date.now();
  };

  const cartMutationFetch = async (url, options = {}, controls = {}) => {
    await waitForCartMutationWindow(controls);
    const response = await fetch(url, options);
    if (isChallengeResponse(response)) markCartChallenge(response);
    else if (controls.clearChallengeOnSuccess && response.ok) clearCartChallengeState();
    return response;
  };

  const drawer = () => document.querySelector(drawerSelector);
  const panel = () => drawer()?.querySelector('.mufod-cart-drawer__panel');

  const syncViewportHeight = () => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    if (viewportHeight > 0) {
      document.documentElement.style.setProperty('--mufod-cart-viewport-height', `${Math.round(viewportHeight)}px`);
    }
  };

  const setStatus = (message = '', error = false) => {
    const node = drawer()?.querySelector('[data-cart-status]');
    if (!node) return;
    node.textContent = message;
    node.style.color = error ? '#b00020' : '#34724a';
  };

  const setBusy = (busy) => {
    cartBusy = busy;
    drawer()?.classList.toggle('is-loading', busy);
  };

  const fetchCart = async () => {
    clearExpiredCartChallenge();
    if (cartChallengeRemainingMs() > 0) throw makeChallengeError();
    const root = drawer();
    const cartUrl = root?.dataset.cartUrl || '/cart';
    // Keep read requests as plain and infrequent as possible. Shopify's 2026
    // cart bot-protection can challenge repeated Ajax Cart API calls, including
    // GET /cart.js. Do not add cache-busting query strings or poll this endpoint.
    const response = await fetch(`${cartUrl}.js`, {
      headers: {'Accept': 'application/json'},
      credentials: 'same-origin'
    });
    if (isChallengeResponse(response)) {
      markCartChallenge(response);
      throw makeChallengeError(response);
    }
    if (!response.ok) {
      const error = new Error(`Unable to load cart (${response.status})`);
      error.mufodHttpStatus = response.status;
      error.mufodRetryAfter = response.headers.get('retry-after') || '';
      throw error;
    }
    return response.json();
  };

  const formatCurrency = (amountCents, currency) => {
    const amount = Number(amountCents || 0) / 100;
    const locale = document.documentElement.lang || navigator.language || 'en-US';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: ['JPY', 'KRW'].includes(currency) ? 0 : 2
      }).format(amount);
    } catch (_) {
      return `${currency || 'USD'} ${amount.toFixed(2)}`;
    }
  };

  const getThresholdInPresentmentCents = (progress, cart) => {
    const baseUsdCents = Number(progress.dataset.thresholdUsdCents || 2999);
    const shopCurrency = String(progress.dataset.shopCurrency || 'USD').toUpperCase();
    const activeCurrency = String(cart.currency || progress.dataset.presentmentCurrency || shopCurrency).toUpperCase();
    const shopifyRate = Number(window.Shopify?.currency?.rate);
    const validRate = Number.isFinite(shopifyRate) && shopifyRate > 0 ? shopifyRate : 1;

    // Shopify.currency.rate converts the shop currency into the customer's active
    // presentment currency. MuFod's shop/base threshold is USD 29.99.
    if (shopCurrency === 'USD') return Math.max(1, Math.round(baseUsdCents * validRate));
    if (activeCurrency === 'USD') return Math.max(1, Math.round(baseUsdCents));

    // Defensive fallback for a future non-USD shop base. The MuFod store currently
    // uses USD, so the branch above is the normal path.
    return Math.max(1, Math.round(baseUsdCents * validRate));
  };

  const updateFreeShippingProgress = (cart, scope = document) => {
    scope.querySelectorAll('[data-free-shipping-progress]').forEach((progress) => {
      const thresholdCents = getThresholdInPresentmentCents(progress, cart);
      const totalCents = Math.max(0, Number(cart.total_price || 0));
      const remainingCents = Math.max(0, thresholdCents - totalCents);
      const percent = thresholdCents > 0 ? Math.min(100, Math.max(0, totalCents / thresholdCents * 100)) : 100;
      const currency = String(cart.currency || progress.dataset.presentmentCurrency || 'USD').toUpperCase();
      const message = progress.querySelector('[data-free-shipping-message]');
      const check = progress.querySelector('[data-free-shipping-check]');
      const track = progress.querySelector('[data-free-shipping-track]');
      const fill = progress.querySelector('[data-free-shipping-fill]');
      const threeBars = Array.from(progress.querySelectorAll('[data-free-shipping-three-bar]'));

      const unlocked = remainingCents <= 0;
      progress.classList.toggle('is-unlocked', unlocked);

      if (message) {
        message.replaceChildren();
        if (unlocked) {
          const strong = document.createElement('strong');
          strong.textContent = progress.dataset.unlockedText || "You've unlocked free shipping";
          message.append(strong);
          if (check) check.hidden = false;
        } else {
          const prefix = progress.dataset.remainingPrefix || "You're";
          const suffix = progress.dataset.remainingSuffix || 'away from free shipping';
          message.append(document.createTextNode(`${prefix} `));
          const strong = document.createElement('strong');
          strong.textContent = formatCurrency(remainingCents, currency);
          message.append(strong, document.createTextNode(` ${suffix}`));
          if (check) check.hidden = true;
        }
      }

      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(percent)));
        track.setAttribute('aria-valuetext', unlocked
          ? (progress.dataset.unlockedText || "You've unlocked free shipping")
          : `${formatCurrency(remainingCents, currency)} ${progress.dataset.remainingSuffix || 'away from free shipping'}`);
      }
      if (threeBars.length === 3) {
        threeBars.forEach((bar, index) => {
          const segmentStart = index * (100 / 3);
          const segmentPercent = Math.min(100, Math.max(0, (percent - segmentStart) * 3));
          bar.style.setProperty('--mufod-three-fill', `${segmentPercent}%`);
        });
      } else if (fill) {
        fill.style.width = `${percent}%`;
      }
    });
  };

  const updateHeaderCount = (cart) => {
    document.querySelectorAll('.header__cart-count').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-empty', cart.item_count === 0);
    });
  };

  const syncCartUI = async (scope = document) => {
    try {
      const cart = await fetchCart();
      updateHeaderCount(cart);
      updateFreeShippingProgress(cart, scope);
      return cart;
    } catch (_) {
      return null;
    }
  };

  const openDrawer = () => {
    const root = drawer();
    if (!root || isEditor()) return;
    lastFocused = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mufod-cart-lock');
    window.requestAnimationFrame(() => panel()?.focus({preventScroll: true}));
  };

  const closeDrawer = () => {
    const root = drawer();
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('mufod-cart-lock');
    lastFocused?.focus?.({preventScroll: true});
  };

  // v2.8.107: render cart HTML independently from the network request that
  // produced it. Cart mutations now request the cart-drawer section in the SAME
  // Shopify Cart API call (bundled section rendering). A successful /cart/add.js
  // must never be reported as an add failure merely because a second section GET
  // was blocked, delayed or returned a transient non-2xx response.
  const applyDrawerHtml = async (html, {open = true, cartData = null} = {}) => {
    const current = drawer();
    if (!current || !html) return false;

    const wasOpen = current.classList.contains('is-open');
    const currentPanel = current.querySelector('.mufod-cart-drawer__panel');
    const currentBody = current.querySelector('[data-cart-drawer-body]');
    const savedScrollTop = currentBody?.scrollTop || 0;

    const active = document.activeElement;
    const focusAttributes = [
      'data-cart-quantity-minus',
      'data-cart-quantity-plus',
      'data-cart-quantity-input',
      'data-cart-remove-line'
    ];
    let focusSelector = '';
    for (const attribute of focusAttributes) {
      const value = active?.getAttribute?.(attribute);
      if (value != null) {
        focusSelector = `[${attribute}="${CSS.escape(value)}"]`;
        break;
      }
    }

    const parsed = new DOMParser().parseFromString(String(html), 'text/html');
    const next = parsed.querySelector(drawerSelector);
    const nextPanel = next?.querySelector('.mufod-cart-drawer__panel');
    if (!next || !currentPanel || !nextPanel) return false;

    currentPanel.innerHTML = nextPanel.innerHTML;
    [...next.attributes].forEach((attribute) => {
      if (attribute.name.startsWith('data-')) current.setAttribute(attribute.name, attribute.value);
    });

    const newBody = current.querySelector('[data-cart-drawer-body]');
    if (newBody) newBody.scrollTop = Math.min(savedScrollTop, newBody.scrollHeight);

    const drawerCartData = (() => {
      const itemCount = Number(current.dataset.cartItemCount);
      const totalPrice = Number(current.dataset.cartTotalPrice);
      const currency = String(current.dataset.cartCurrency || '');
      if (!Number.isFinite(itemCount) || !Number.isFinite(totalPrice)) return null;
      return {item_count: itemCount, total_price: totalPrice, currency};
    })();
    const uiCartData = cartData && Number.isFinite(Number(cartData.item_count)) ? cartData : drawerCartData;
    if (uiCartData) {
      updateHeaderCount(uiCartData);
      updateFreeShippingProgress(uiCartData, document);
    }

    if (wasOpen) {
      current.classList.add('is-open');
      current.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('mufod-cart-lock');
      if (focusSelector) current.querySelector(focusSelector)?.focus({preventScroll: true});
    } else if (open) {
      openDrawer();
    }
    return true;
  };

  const bundledDrawerHtml = (result) => {
    const html = result?.sections?.['cart-drawer'];
    return typeof html === 'string' && html.trim() ? html : '';
  };

  const variantQuantityInRenderedCart = (variantId) => {
    const id = String(variantId || '');
    if (!id) return 0;
    return [...(drawer()?.querySelectorAll(`[data-cart-variant-id="${CSS.escape(id)}"]`) || [])]
      .reduce((sum, node) => sum + Math.max(0, Math.floor(Number(node.dataset.cartCurrentQuantity || 0))), 0);
  };

  const renderedCartSnapshot = () => {
    const root = drawer();
    if (!root) return null;
    const itemCount = Number(root.dataset.cartItemCount);
    const totalPrice = Number(root.dataset.cartTotalPrice);
    const currency = String(root.dataset.cartCurrency || '');
    if (!Number.isFinite(itemCount) || !Number.isFinite(totalPrice)) return null;
    return {item_count: itemCount, total_price: totalPrice, currency};
  };

  const reconcileVariantMutation = async ({variantId, beforeQuantity, expectedIncrease, attempts = 3} = {}) => {
    let lastCart = null;
    for (let attempt = 0; attempt < Math.max(1, attempts); attempt += 1) {
      if (attempt) await sleep(120 * attempt);
      try {
        lastCart = await fetchCart();
        const current = variantQuantityInCartJson(lastCart, variantId);
        if (current >= beforeQuantity + expectedIncrease) {
          return {confirmed: true, changed: true, current, cart: lastCart};
        }
        if (current !== beforeQuantity) {
          return {confirmed: true, changed: true, current, cart: lastCart};
        }
      } catch (_) {
        // A later read may succeed; never issue a second mutation here.
      }
    }
    if (lastCart) {
      const current = variantQuantityInCartJson(lastCart, variantId);
      return {confirmed: true, changed: current !== beforeQuantity, current, cart: lastCart};
    }
    return {confirmed: false, changed: false, current: beforeQuantity, cart: null};
  };

  const refreshDrawer = async ({open = true, retries = 0} = {}) => {
    clearExpiredCartChallenge();
    if (cartChallengeRemainingMs() > 0) throw makeChallengeError();
    const current = drawer();
    if (!current) return false;
    const rootUrl = current.dataset.rootUrl || '/';
    const separator = String(rootUrl).includes('?') ? '&' : '?';
    let lastError = new Error('Unable to refresh cart');

    // Use the official multi-section rendering shape. Static section IDs use the
    // section filename, so cart-drawer is stable across templates/locales.
    for (let attempt = 0; attempt <= Math.max(0, retries); attempt += 1) {
      try {
        const response = await fetch(`${rootUrl}${separator}sections=cart-drawer`, {
          headers: {'Accept': 'application/json'},
          credentials: 'same-origin'
        });
        if (isChallengeResponse(response)) {
          markCartChallenge(response);
          throw makeChallengeError(response);
        }
        if (!response.ok) throw new Error(`Unable to refresh cart (${response.status})`);
        const payload = await response.json();
        const html = payload?.['cart-drawer'];
        if (typeof html !== 'string' || !html.trim()) throw new Error('Cart drawer markup unavailable');
        if (await applyDrawerHtml(html, {open})) return true;
        throw new Error('Cart drawer markup unavailable');
      } catch (error) {
        lastError = error instanceof Error ? error : lastError;
        if (attempt < retries) await new Promise((resolve) => window.setTimeout(resolve, 140 * (attempt + 1)));
      }
    }
    throw lastError;
  };

  const productCartSelection = (form) => {
    if (!form.matches('[data-product-form]')) return null;

    // v2.8.103: product-page-v270.js resolves the complete visible option tuple
    // to one exact Shopify Variant immediately before submit. Cart code consumes
    // only that resolved Variant ID + quantity; it never guesses from Color,
    // Capacity, Size, Pack, a previous select value, or an aggregate stock value.
    const quantityField = form.querySelector('[data-quantity-input], [name="quantity"]');
    const variantReady = form.dataset.mufodVariantReady === 'true';
    const variantId = Number(form.dataset.mufodVariantId || 0);
    const quantity = Math.max(1, Math.floor(Number(form.dataset.mufodQuantity || quantityField?.value || 1)));

    if (!variantReady || !Number.isSafeInteger(variantId) || variantId <= 0) {
      const error = new Error('Selected option is unavailable. Please choose another option.');
      error.mufodSelectionError = true;
      throw error;
    }
    return {variantId, quantity};
  };

  // v2.8.113: use Shopify's current Dawn transport shape rather than the
  // documented .js JSON route that was returning HAR-confirmed Cloudflare 429s
  // for this storefront session. Keep the exact resolved Variant ID, but send a
  // fresh FormData payload to routes.cart_add_url (/cart/add) with the same
  // X-Requested-With + sections + sections_url pattern used by Dawn.
  const exactProductFormData = (selection) => {
    const data = new FormData();
    data.append('id', String(selection.variantId));
    data.append('quantity', String(selection.quantity));
    data.append('sections', 'cart-drawer');
    data.append('sections_url', window.location.pathname || '/');
    return data;
  };

  const prepareExactProductForm = (form) => {
    if (!form?.matches?.('[data-product-form]')) return;
    // v2.8.104: ask the product controller to synchronously re-resolve the
    // complete visible option tuple immediately before any cart request. This
    // custom event is isolated from third-party submit/click handlers.
    const detail = {ready: false, variantId: 0, quantity: 1};
    form.dispatchEvent(new CustomEvent('mufod:prepare-cart-add', {detail}));
  };

  const readCartAddResponse = async (response) => {
    const raw = await response.text();
    let result = {};
    let parsedJson = false;
    try {
      result = raw ? JSON.parse(raw) : {};
      parsedJson = true;
    } catch (_) {
      result = {};
    }
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    return {response, raw, result, parsedJson, contentType};
  };

  const technicalAddFailure = ({response, raw, parsedJson, contentType}) => {
    if (!response) return true;
    const status = Number(response.status || 0);
    const rawText = String(raw || '');
    const looksLikeChallengeHtml = !parsedJson
      && !String(contentType || '').includes('application/json')
      && (/<(?:!doctype|html|body)\b/i.test(rawText)
        || /cdn-cgi|cf-chl|verifying your connection|connection needs to be verified/i.test(rawText));
    return looksLikeChallengeHtml || status === 403 || status === 408 || status === 409
      || status === 425 || status === 429 || status >= 500;
  };

  const verificationAddFailure = ({response, raw, parsedJson, contentType}) => {
    const status = Number(response?.status || 0);
    const text = String(raw || '');
    const challengeMarkup = !parsedJson
      && !String(contentType || '').includes('application/json')
      && /cdn-cgi|cf-chl|verifying your connection|connection needs to be verified/i.test(text);
    return status === 403 || status === 429 || challengeMarkup;
  };

  // Shopify's successful /cart/add.js response can contain either the added line
  // items or a cart-shaped payload when bundled section rendering is requested.
  // Never assume items[0] is the item just added: once the cart contains several
  // lines, the selected Variant can appear at any index. Also prefer variant_id
  // over the line item's id field; they are not guaranteed to be the same value.
  const responseContainsVariant = (result, variantId) => {
    const wanted = Number(variantId || 0);
    if (!Number.isSafeInteger(wanted) || wanted <= 0) return false;

    const directVariantId = Number(result?.variant_id || 0);
    if (Number.isSafeInteger(directVariantId) && directVariantId === wanted) return true;

    if (Array.isArray(result?.items)) {
      return result.items.some((item) => {
        const candidate = Number(item?.variant_id || 0);
        if (Number.isSafeInteger(candidate) && candidate > 0) return candidate === wanted;
        // Legacy fallback only when variant_id is absent.
        const legacyId = Number(item?.id || 0);
        return Number.isSafeInteger(legacyId) && legacyId === wanted;
      });
    }

    // Legacy single-line response fallback.
    const legacyDirectId = Number(result?.id || 0);
    return Number.isSafeInteger(legacyDirectId) && legacyDirectId === wanted;
  };

  const exactAddSucceeded = (addResult, selection) => {
    if (!addResult?.response?.ok || technicalAddFailure(addResult)) return false;
    return responseContainsVariant(addResult.result, selection?.variantId);
  };

  const submitExactCartAdd = async (root, form, selection) => {
    // v2.8.113: one mutation per customer action, using Shopify Dawn's current
    // storefront transport shape: POST /cart/add, FormData, X-Requested-With,
    // and bundled section rendering in the same request. No automatic POST retry.
    return readCartAddResponse(await cartMutationFetch(root.dataset.cartAddUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: exactProductFormData(selection)
    }));
  };


  const addToCart = async (form, submitter = null) => {
    const root = drawer();
    if (!root || cartBusy) return;
    const transactionId = ++cartTransactionId;
    setBusy(true);
    setStatus('');
    const productRoot = form.closest('[data-mufod-product]');
    if (productRoot) productRoot.dataset.cartTransactionLock = 'true';
    const button = submitter || form.querySelector('[type="submit"]');
    const original = button?.innerHTML;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    let attemptedVariantId = 0;
    let attemptedQuantity = 1;
    let existingVariantQuantityBeforeAdd = 0;
    try {
      prepareExactProductForm(form);
      const strictSelection = productCartSelection(form);
      attemptedVariantId = Number(strictSelection?.variantId || form.querySelector('[name="id"]')?.value || 0);
      attemptedQuantity = Math.max(1, Math.floor(Number(strictSelection?.quantity || form.querySelector('[name="quantity"]')?.value || 1)));

      // v2.8.106: inventory is never a fixed number. The product controller puts
      // THIS exact Shopify Variant's current inventory_quantity on the quantity
      // input max. Before adding, read the authoritative cart and sum every cart
      // line using the same Variant ID. Remaining addable stock is therefore:
      // exact Variant inventory - quantity of that exact Variant already in cart.
      const preflightQuantityField = form.querySelector('[data-quantity-input], [name="quantity"]');
      const preflightMaxRaw = preflightQuantityField?.getAttribute('max');
      const preflightMax = preflightMaxRaw == null || preflightMaxRaw === ''
        ? null
        : Math.max(0, Math.floor(Number(preflightMaxRaw)));

      if (attemptedVariantId > 0) {
        // The drawer is server-rendered on page load and replaced after every
        // successful mutation. Use that current snapshot for instant preflight.
        // Shopify remains authoritative and will reject a stale cross-tab cart.
        existingVariantQuantityBeforeAdd = variantQuantityInRenderedCart(attemptedVariantId);
      }

      const desiredTotalBeforeAdd = existingVariantQuantityBeforeAdd + attemptedQuantity;
      if (Number.isFinite(preflightMax) && desiredTotalBeforeAdd > preflightMax) {
        const remaining = Math.max(0, preflightMax - existingVariantQuantityBeforeAdd);
        const stockError = new Error(remaining > 0
          ? `Only ${remaining} more available for this selected option.`
          : stockLimitMessage(preflightMax));
        stockError.mufodInventoryError = true;
        stockError.mufodCartConstraint = true;
        stockError.mufodVariantId = attemptedVariantId;
        throw stockError;
      }

      let addResult;
      if (strictSelection) {
        addResult = await submitExactCartAdd(root, form, strictSelection);
      } else {
        const genericData = new FormData(form);
        genericData.set('sections', 'cart-drawer');
        genericData.set('sections_url', window.location.pathname || '/');
        addResult = await readCartAddResponse(await cartMutationFetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: genericData
        }));
      }

      const {response, raw, result} = addResult;
      const responseConfirmed = strictSelection
        ? exactAddSucceeded(addResult, strictSelection)
        : (response.ok && !technicalAddFailure(addResult));

      if (!responseConfirmed) {
        // v2.8.111: NEVER hard-reload the storefront when Shopify/Cloudflare
        // challenges an Ajax Cart request. v2.8.109/110 intentionally reloaded the
        // page here, which surfaced the full-page “Your connection needs to be
        // verified” challenge to real shoppers. Keep the current product page
        // stable, stop issuing further Cart API calls for this transaction, and
        // let the shopper retry manually after the platform challenge window clears.
        if (verificationAddFailure(addResult)) {
          markCartChallenge(addResult?.response);
          const verificationError = makeChallengeError(addResult?.response);
          verificationError.mufodVariantId = attemptedVariantId;
          throw verificationError;
        }

        // For non-verification ambiguity, perform only ONE read-only reconciliation.
        // More repeated Cart API reads can themselves trigger Shopify bot protection.
        const reconciliation = attemptedVariantId > 0
          ? await reconcileVariantMutation({
              variantId: attemptedVariantId,
              beforeQuantity: existingVariantQuantityBeforeAdd,
              expectedIncrease: attemptedQuantity,
              attempts: 1
            })
          : {confirmed: false, changed: false, cart: null};

        if (reconciliation.changed) {
          let rendered = false;
          try { rendered = await refreshDrawer({open: true, retries: 0}); } catch (_) { rendered = false; }
          if (!rendered) {
            if (reconciliation.cart) {
              updateHeaderCount(reconciliation.cart);
              updateFreeShippingProgress(reconciliation.cart, document);
            }
            openDrawer();
          }
          return;
        }

        const contentType = String(response.headers.get('content-type') || '').toLowerCase();
        const rawLooksLikeHtml = /<(?:!doctype|html|body|script)\b/i.test(raw)
          || /cdn-cgi|cf-chl|verifying your connection/i.test(raw);
        const structuredMessage = result.description || result.message || result.error || '';
        const soldOut = /sold out|not enough|insufficient|unavailable|maximum quantity|available quantity|inventory|stock/i.test(String(structuredMessage || raw));
        const safeMessage = structuredMessage
          || (soldOut ? 'This option could not be added because the selected quantity is unavailable.' : '')
          || (!rawLooksLikeHtml && contentType.includes('json') ? raw : '')
          || (reconciliation.confirmed
            ? 'Unable to add item. Please try again.'
            : 'Unable to verify the cart connection. Please refresh and try again.');
        const cartError = new Error(safeMessage);
        cartError.mufodInventoryError = soldOut || inventoryError(safeMessage);
        cartError.mufodCartConstraint = response.status === 400 || response.status === 422;
        cartError.mufodTechnicalAddError = technicalAddFailure(addResult) || !reconciliation.confirmed;
        cartError.mufodHttpStatus = response.status;
        cartError.mufodVariantId = attemptedVariantId;
        console.warn('[MuFod cart/add not confirmed]', {
          transactionId,
          variantId: attemptedVariantId,
          quantity: attemptedQuantity,
          status: response.status,
          message: safeMessage,
          reconciled: reconciliation.confirmed
        });
        throw cartError;
      }

      // v2.8.107: use the section HTML returned by this very cart/add request.
      // If Shopify returns null for the bundled section, the cart mutation can still
      // have succeeded; a fallback refresh is display-only and is never converted
      // into an add-to-cart failure.
      const bundledHtml = bundledDrawerHtml(result);
      let rendered = false;
      if (bundledHtml) {
        try { rendered = await applyDrawerHtml(bundledHtml, {open: true, cartData: result}); } catch (_) { rendered = false; }
      }
      if (!rendered) {
        try { rendered = await refreshDrawer({open: true, retries: 0}); } catch (_) { rendered = false; }
      }
      if (!rendered) {
        // Mutation is already confirmed. A visual rendering failure must never be
        // presented as an add failure and must never trigger a duplicate mutation.
        if (Number.isFinite(Number(result?.item_count))) {
          updateHeaderCount(result);
          updateFreeShippingProgress(result, document);
        }
        openDrawer();
      }
    } catch (error) {
      const message = (error.mufodVerificationBlocked || error.mufodCooldown)
        ? cartChallengeMessage()
        : (error.message || 'Unable to add item');
      const variantId = Number(error.mufodVariantId || attemptedVariantId || 0);
      const quantityField = form.querySelector('[data-quantity-input], [name="quantity"]');
      const productInventoryMaxRaw = quantityField?.getAttribute('max');
      const productInventoryMax = productInventoryMaxRaw == null || productInventoryMaxRaw === ''
        ? null
        : Math.max(0, Math.floor(Number(productInventoryMaxRaw)));
      let shownOnLine = false;

      // v2.8.108: there is deliberately no automatic second POST and no native
      // fallback submit here. Ambiguous mutation responses were reconciled above.
      // One customer action can therefore never silently add the same Variant twice.

      // Do not issue another Cart/Section request after a verification/429 failure.
      // Keep the current page stable; never navigate the shopper into a Cloudflare
      // challenge screen from theme JavaScript.
      if (!error.mufodTechnicalAddError) {
        try { await refreshDrawer({open: true}); } catch (_) { openDrawer(); }
      } else {
        openDrawer();
      }

      if ((error.mufodInventoryError || error.mufodCartConstraint) && variantId > 0) {
        const lineNode = drawer()?.querySelector(`[data-cart-variant-id="${CSS.escape(String(variantId))}"]`);
        const line = lineNode?.dataset.cartLine;
        const lineMax = line ? cartLineMax(line) : null;
        const max = lineMax ?? (Number.isFinite(productInventoryMax) ? productInventoryMax : null);
        const desiredTotal = existingVariantQuantityBeforeAdd + Math.max(1, attemptedQuantity);

        if (line && max != null && desiredTotal > max) {
          shownOnLine = showLineMessage(line, stockLimitMessage(max));
        } else if (line) {
          clearLineMessage(line);
        }
      }

      // Inventory/quantity errors stay off the footer. Only a true transport/WAF/
      // technical failure uses the global status after the exact add retry failed.
      if (!shownOnLine && !error.mufodInventoryError && !error.mufodCartConstraint) setStatus(message, true);
      else setStatus('');
      openDrawer();
    } finally {
      if (productRoot) delete productRoot.dataset.cartTransactionLock;
      setBusy(false);
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (original != null && document.contains(button)) button.innerHTML = original;
      }
    }
  };

  const cartLine = (line) => drawer()?.querySelector(`[data-cart-line="${CSS.escape(String(line))}"]`);

  const cartLineByKey = (key) => {
    if (!key) return null;
    return drawer()?.querySelector(`[data-cart-line-key="${CSS.escape(String(key))}"]`);
  };

  const cartLineKey = (line) => cartLine(line)?.dataset.cartLineKey || '';

  const cartLineMax = (line) => {
    const node = cartLine(line);
    const raw = node?.dataset.cartInventoryMax;
    if (raw == null || raw === '') return null;
    const stock = Math.floor(Number(raw));
    if (!Number.isFinite(stock) || stock < 0) return null;

    // v2.8.106: stock belongs to the exact Variant ID. If the same Variant is
    // split across multiple cart lines, reserve the quantities already present
    // on the sibling lines so their combined total can never exceed Variant stock.
    const variantId = String(node?.dataset.cartVariantId || '');
    if (!variantId) return stock;
    const usedByOtherLines = [...(drawer()?.querySelectorAll(`[data-cart-variant-id="${CSS.escape(variantId)}"]`) || [])]
      .filter((other) => other !== node)
      .reduce((sum, other) => sum + Math.max(0, Math.floor(Number(other.dataset.cartCurrentQuantity || 0))), 0);
    return Math.max(0, stock - usedByOtherLines);
  };

  const cartLineCurrent = (line) => {
    const node = cartLine(line);
    const raw = node?.dataset.cartCurrentQuantity;
    const current = Math.floor(Number(raw));
    if (Number.isFinite(current) && current >= 0) return current;
    const input = node?.querySelector(`[data-cart-quantity-input="${CSS.escape(String(line))}"]`);
    return Math.max(0, Math.floor(Number(input?.value || 0)));
  };

  const showLineMessage = (line, message = '') => {
    const node = cartLine(line)?.querySelector(`[data-cart-line-message="${CSS.escape(String(line))}"]`);
    if (!node) return false;
    node.textContent = message;
    node.hidden = !message;
    return true;
  };

  const clearLineMessage = (line) => showLineMessage(line, '');

  const stockLimitMessage = (max) => {
    const safeMax = Math.max(0, Math.floor(Number(max) || 0));
    return `Maximum available quantity: ${safeMax}`;
  };

  // v2.8.105: the standalone /cart page is a separate quantity surface from the
  // drawer. Give it the same exact Variant/SKU inventory ceiling and never allow
  // checkout while a rendered cart line exceeds managed stock.
  const cartPageForm = () => document.querySelector('[data-cart-page-form]');
  const cartPageLine = (line) => cartPageForm()?.querySelector(`[data-cart-page-line="${CSS.escape(String(line))}"]`);
  const cartPageLineMax = (line) => {
    const node = cartPageLine(line);
    const raw = node?.dataset.cartPageInventoryMax;
    if (raw == null || raw === '') return null;
    const stock = Math.floor(Number(raw));
    if (!Number.isFinite(stock) || stock < 0) return null;

    const variantId = String(node?.dataset.cartPageVariantId || '');
    if (!variantId) return stock;
    const form = cartPageForm();
    const usedByOtherLines = [...(form?.querySelectorAll(`[data-cart-page-variant-id="${CSS.escape(variantId)}"]`) || [])]
      .filter((other) => other !== node)
      .reduce((sum, other) => {
        const siblingInput = other.querySelector('[data-cart-page-quantity-input]');
        const quantity = siblingInput?.value ?? other.dataset.cartPageCurrentQuantity ?? 0;
        return sum + Math.max(0, Math.floor(Number(quantity || 0)));
      }, 0);
    return Math.max(0, stock - usedByOtherLines);
  };
  const cartPageLineCurrent = (line) => {
    const node = cartPageLine(line);
    const input = node?.querySelector(`[data-cart-page-quantity-input="${CSS.escape(String(line))}"]`);
    return Math.max(0, Math.floor(Number(input?.value ?? node?.dataset.cartPageCurrentQuantity ?? 0) || 0));
  };
  const showCartPageLineMessage = (line, message = '') => {
    const node = cartPageLine(line)?.querySelector(`[data-cart-page-line-message="${CSS.escape(String(line))}"]`);
    if (!node) return false;
    node.textContent = message;
    node.hidden = !message;
    return true;
  };
  const cartPageOverstockLines = ({useRenderedQuantity = true} = {}) => {
    const form = cartPageForm();
    if (!form) return [];
    return [...form.querySelectorAll('[data-cart-page-line]')].map((node) => {
      const line = String(node.dataset.cartPageLine || '');
      const max = cartPageLineMax(line);
      const rendered = cartPageLineCurrent(line);
      const serverCurrent = Math.max(0, Math.floor(Number(node.dataset.cartPageCurrentQuantity || 0) || 0));
      const current = useRenderedQuantity ? rendered : serverCurrent;
      return {node, line, max, current, serverCurrent, key: node.dataset.cartPageLineKey || ''};
    }).filter((entry) => entry.max != null && entry.current > entry.max);
  };
  const setCartPageCheckoutDisabled = (disabled) => {
    cartPageForm()?.querySelectorAll('[data-cart-page-checkout]').forEach((button) => {
      button.disabled = Boolean(disabled);
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  };
  const repairCartPageServerOverstock = async () => {
    const form = cartPageForm();
    if (!form) return false;
    const invalid = cartPageOverstockLines({useRenderedQuantity: false});
    if (!invalid.length) return false;

    // The cart can arrive here already invalid (for example an older theme allowed
    // 120 while the exact Variant stock is 60). Correct that stale server cart
    // immediately, then reload once so line prices, subtotal and header count agree.
    setCartPageCheckoutDisabled(true);
    invalid.forEach(({line, max}) => {
      const input = cartPageLine(line)?.querySelector(`[data-cart-page-quantity-input="${CSS.escape(String(line))}"]`);
      if (input) input.value = String(max);
      showCartPageLineMessage(line, stockLimitMessage(max));
    });

    const changeUrl = form.dataset.cartPageChangeUrl || '/cart/change.js';
    try {
      for (const entry of invalid) {
        const payload = entry.key
          ? {id: entry.key, quantity: entry.max}
          : {line: Number(entry.line), quantity: entry.max};
        const response = await fetch(changeUrl, {
          method: 'POST',
          headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Unable to correct cart quantity');
      }
      window.location.reload();
      return true;
    } catch (_) {
      invalid.forEach(({line, max}) => showCartPageLineMessage(line, stockLimitMessage(max)));
      setCartPageCheckoutDisabled(true);
      return false;
    }
  };

  const updateDrawerSummaryFromCart = (cart) => {
    if (!cart || !Number.isFinite(Number(cart.item_count))) return;
    const root = drawer();
    if (!root) return;
    root.dataset.cartItemCount = String(Math.max(0, Number(cart.item_count || 0)));
    root.dataset.cartTotalPrice = String(Math.max(0, Number(cart.total_price || 0)));
    if (cart.currency) root.dataset.cartCurrency = String(cart.currency);
    root.querySelector('[data-cart-drawer-count-label]')?.replaceChildren(document.createTextNode(`(${Math.max(0, Number(cart.item_count || 0))})`));
    const subtotal = root.querySelector('.mufod-cart-drawer__subtotal strong');
    if (subtotal) subtotal.textContent = formatCurrency(cart.total_price || 0, cart.currency || root.dataset.cartCurrency || 'USD');
    updateHeaderCount(cart);
    updateFreeShippingProgress(cart, document);
  };

  const renderEmptyDrawerLocally = (cart = null) => {
    const root = drawer();
    if (!root) return;
    const body = root.querySelector('[data-cart-drawer-body]');
    const tpl = root.querySelector('template[data-cart-empty-template]');
    if (body && tpl?.content) body.replaceChildren(tpl.content.cloneNode(true));
    root.querySelector('[data-cart-drawer-footer]')?.setAttribute('hidden', '');
    const progress = root.querySelector('[data-free-shipping-progress]');
    if (progress) progress.setAttribute('hidden', '');
    const snapshot = cart || {item_count: 0, total_price: 0, currency: root.dataset.cartCurrency || 'USD'};
    updateDrawerSummaryFromCart(snapshot);
  };

  const lineRemovalMessage = () => 'Shopify temporarily blocked this removal request. Please try Remove again in a moment.';

  const markLineRemoving = (entry, removing = true) => {
    const node = entry?.key ? cartLineByKey(entry.key) : cartLine(entry?.line);
    if (!node) return null;
    node.classList.toggle('is-removing', removing);
    if (removing) {
      node.setAttribute('aria-busy', 'true');
      node.querySelectorAll('button,input,a').forEach((control) => {
        if ('disabled' in control) control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      });
      const input = node.querySelector('[data-cart-quantity-input]');
      if (input) input.value = '0';
      const line = node.dataset.cartLine;
      if (line) clearLineMessage(line);
    } else {
      node.removeAttribute('aria-busy');
      node.querySelectorAll('button,input,a').forEach((control) => {
        if ('disabled' in control) control.disabled = false;
        control.removeAttribute('aria-disabled');
      });
    }
    return node;
  };

  const scheduleRemovalFlush = () => {
    if (removalFlushTimer) window.clearTimeout(removalFlushTimer);
    if (!pendingRemovals.size) {
      removalBatchStartedAt = 0;
      return;
    }
    if (!removalBatchStartedAt) removalBatchStartedAt = Date.now();
    const elapsed = Date.now() - removalBatchStartedAt;
    const delay = Math.max(0, Math.min(CART_REMOVE_QUIET_MS, CART_REMOVE_MAX_WAIT_MS - elapsed));
    removalFlushTimer = window.setTimeout(() => flushPendingRemovals(), delay);
  };

  const queueLineRemoval = (line) => {
    const node = cartLine(line);
    if (!node) return;
    const key = String(node.dataset.cartLineKey || '');
    if (!key) {
      showLineMessage(line, 'Unable to identify this cart item. Please refresh the page.');
      return;
    }
    // v2.8.116: deleting/zeroing a line is always allowed to TRY immediately.
    // A previous add-to-cart 429 cooldown must never locally block Remove.
    // If Shopify is still actively challenging the update endpoint, the server
    // response below will handle that specific failure without hiding the control.
    if (pendingRemovals.has(key)) return;
    const entry = {
      key,
      line: String(node.dataset.cartLine || line),
      beforeQuantity: cartLineCurrent(line)
    };
    pendingRemovals.set(key, entry);
    markLineRemoving(entry, true);
    setStatus('');
    scheduleRemovalFlush();
  };

  const flushPendingRemovals = async () => {
    removalFlushTimer = 0;
    if (removalBatchInFlight || !pendingRemovals.size) return;
    if (cartBusy) {
      removalFlushTimer = window.setTimeout(() => flushPendingRemovals(), 160);
      return;
    }

    const root = drawer();
    if (!root) return;
    const entries = [...pendingRemovals.values()];
    pendingRemovals.clear();
    removalBatchStartedAt = 0;
    removalBatchInFlight = true;
    setBusy(true);
    setStatus('');

    try {
      // v2.8.116: Remove is intentionally exempt from the add/increase cooldown.
      // Send the lightest possible update: only stable line-item keys -> 0. Do not
      // ask Shopify to render cart-drawer HTML during deletion. That extra section
      // work is unnecessary and increases request weight during rapid cart cleanup.
      const formData = new FormData();
      entries.forEach((entry) => formData.append(`updates[${entry.key}]`, '0'));

      const updateUrl = root.dataset.cartUpdateUrl || `${String(root.dataset.rootUrl || '/').replace(/\/?$/, '/')}cart/update`;
      const response = await cartMutationFetch(updateUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: formData
      }, {allowDuringChallenge: true, clearChallengeOnSuccess: true});
      const raw = await response.text();
      let result = {};
      try { result = raw ? JSON.parse(raw) : {}; } catch (_) { result = {}; }

      if (!response.ok) {
        if (isChallengeResponse(response)) {
          markCartChallenge(response);
          throw makeChallengeError(response);
        }
        const error = new Error(result.description || result.message || 'Unable to remove item');
        error.mufodHttpStatus = response.status;
        throw error;
      }

      // The lightweight cart/update response is authoritative. Remove affected
      // cards locally and update count/subtotal/free-shipping from returned cart
      // JSON. No second section GET, no drawer rerender, no extra Cart API call.
      entries.forEach((entry) => {
        const current = cartLineByKey(entry.key);
        if (current) current.remove();
      });
      if (Number(result?.item_count || 0) <= 0) renderEmptyDrawerLocally(result);
      else updateDrawerSummaryFromCart(result);
      openDrawer();
    } catch (error) {
      const challenge = Boolean(error?.mufodVerificationBlocked || error?.mufodCooldown);
      entries.forEach((entry) => {
        const node = markLineRemoving(entry, false);
        if (!node) return;
        const currentLine = node.dataset.cartLine || entry.line;
        showLineMessage(currentLine, challenge ? lineRemovalMessage() : 'Unable to remove item. Please try again.');
        const input = node.querySelector('[data-cart-quantity-input]');
        if (input) input.value = String(entry.beforeQuantity);
      });
      // Remove failures stay on the affected product cards. Never use the global
      // footer error for delete/zero-quantity actions.
      setStatus('');
      openDrawer();
    } finally {
      removalBatchInFlight = false;
      setBusy(false);
      if (pendingRemovals.size) scheduleRemovalFlush();
    }
  };

  const inventoryError = (message = '') => /only .*items?|availability|available quantity|sold out|not enough|insufficient|unavailable|inventory|stock/i.test(String(message));

  // v2.8.96: the cart inventory ceiling is the current line Variant/SKU's
  // Liquid inventory_quantity only. Never replace it with a generic Shopify
  // add-to-cart response limit, because that can be a store-level cap (for
  // example 50) rather than this Variant's real stock.

  const changeLine = async (line, quantity, {lineMessage = '', isRemove = false} = {}) => {
    const root = drawer();
    if (!root) return;
    const normalizedQuantity = Math.max(0, Math.floor(Number(quantity) || 0));

    // v2.8.116: every zero-quantity path (Remove, 1 -> 0, manual 0, stock -> 0)
    // uses the optimistic batched removal queue. The card reacts instantly with
    // a spinner, while rapid deletes stay interactive and are combined into one cart/update mutation after a short quiet window.
    if (normalizedQuantity === 0) {
      queueLineRemoval(line);
      return;
    }
    if (cartBusy || removalBatchInFlight) return;
    const lineKey = cartLineKey(line);
    const beforeQuantity = cartLineCurrent(line);
    setBusy(true);
    setStatus('');
    try {
      // v2.8.98: use Shopify's stable line-item key whenever possible. A numeric
      // line position can become stale after AJAX refreshes, app-added items or
      // cart reordering, which made Remove intermittently target the wrong line.
      const payload = lineKey
        ? {id: lineKey, quantity: normalizedQuantity}
        : {line: Number(line), quantity: normalizedQuantity};
      // v2.8.107: cart/change and cart drawer HTML are one atomic request. This
      // removes the old change -> second section GET race that could leave the
      // drawer stale even though Shopify had already accepted the quantity change.
      payload.sections = ['cart-drawer'];
      payload.sections_url = window.location.pathname || '/';
      const response = await cartMutationFetch(root.dataset.cartChangeUrl, {
        method: 'POST',
        headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      const raw = await response.text();
      let result = {};
      try { result = raw ? JSON.parse(raw) : {}; } catch (_) { result = {}; }

      if (!response.ok) {
        if (isChallengeResponse(response)) {
          markCartChallenge(response);
          throw makeChallengeError(response);
        }
        // cart/change can have the same “mutation succeeded, section response failed”
        // ambiguity as cart/add. Reconcile the stable line-item key before showing an
        // error; never send a second change mutation automatically.
        let reconciledCart = null;
        let actualQuantity = null;
        if (lineKey) {
          for (let attempt = 0; attempt < 1; attempt += 1) {
            if (attempt) await sleep(120 * attempt);
            try {
              reconciledCart = await fetchCart();
              actualQuantity = lineQuantityInCartJson(reconciledCart, lineKey);
              if (actualQuantity === normalizedQuantity) break;
            } catch (_) {}
          }
        }
        if (actualQuantity === normalizedQuantity) {
          let rendered = false;
          try { rendered = await refreshDrawer({open: true, retries: 0}); } catch (_) { rendered = false; }
          if (!rendered && reconciledCart) {
            updateHeaderCount(reconciledCart);
            updateFreeShippingProgress(reconciledCart, document);
            openDrawer();
          }
          return;
        }

        const cartError = new Error(result.description || result.message || 'Unable to update cart');
        cartError.mufodCartConstraint = response.status === 400 || response.status === 422;
        cartError.mufodHttpStatus = response.status;
        cartError.mufodBeforeQuantity = beforeQuantity;
        throw cartError;
      }

      let rendered = false;
      const html = bundledDrawerHtml(result);
      if (html) {
        try { rendered = await applyDrawerHtml(html, {open: true, cartData: result}); } catch (_) { rendered = false; }
      }
      if (!rendered) {
        try { rendered = await refreshDrawer({open: true, retries: 0}); } catch (_) { rendered = false; }
      }
      if (!rendered) {
        // The mutation is confirmed. Keep success semantics even if display
        // reconciliation is temporarily unavailable; never repeat the mutation.
        if (Number.isFinite(Number(result?.item_count))) {
          updateHeaderCount(result);
          updateFreeShippingProgress(result, document);
        }
        openDrawer();
        return;
      }
      if (lineMessage) showLineMessage(line, lineMessage);
    } catch (error) {
      const message = error.message || 'Unable to update cart';
      const requested = normalizedQuantity;
      let max = cartLineMax(line);

      // A 429/Cloudflare verification response is an edge rejection, not an
      // inventory problem. Do not follow it with /cart.js or section requests.
      if (error.mufodVerificationBlocked || error.mufodCooldown) {
        setStatus(cartChallengeMessage(), true);
        openDrawer();
        return;
      }

      // Restore Shopify's authoritative cart only for ordinary rejected changes.
      try { await refreshDrawer({open: true}); } catch (_) {}

      // A Remove failure should never fall through to the footer status. If the
      // keyed line no longer exists after refresh, Shopify already removed it.
      // Otherwise keep any technical retry message on that product card only.
      if (isRemove || requested === 0) {
        const remainingNode = lineKey ? cartLineByKey(lineKey) : cartLine(line);
        if (!remainingNode) {
          setStatus('');
          return;
        }
        const remainingLine = remainingNode.dataset.cartLine || line;
        showLineMessage(remainingLine, 'Unable to remove item. Please try again.');
        setStatus('');
        return;
      }

      const refreshedNode = lineKey ? cartLineByKey(lineKey) : cartLine(line);
      const refreshedLine = refreshedNode?.dataset.cartLine || line;
      max = cartLineMax(refreshedLine) ?? max;

      if (max != null && requested > max) {
        showLineMessage(refreshedLine, stockLimitMessage(max));
        setStatus('');
      } else {
        clearLineMessage(refreshedLine);
        if (inventoryError(message) || error.mufodCartConstraint) {
          // Do not show inventory/quantity constraints in the drawer footer.
          setStatus('');
        } else {
          setStatus(message, true);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  // v2.8.98: pointerdown fires before an edited quantity input loses focus.
  // Record a Remove intent so the input's ensuing change event cannot start a
  // competing quantity request that blocks the actual Remove click.
  document.addEventListener('pointerdown', (event) => {
    const remove = event.target.closest('[data-cart-remove-line]');
    if (!remove) return;
    removeIntent = {
      line: String(remove.dataset.cartRemoveLine || ''),
      at: Date.now()
    };
  }, true);

  document.addEventListener('click', (event) => {
    const cartTrigger = event.target.closest('[data-cart-drawer-open]');
    if (cartTrigger && !isEditor()) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const drawerCheckout = event.target.closest('[data-cart-drawer-checkout]');
    if (drawerCheckout) {
      const invalidLine = [...(drawer()?.querySelectorAll('[data-cart-line]') || [])].find((node) => {
        const line = node.dataset.cartLine;
        const max = cartLineMax(line);
        return max != null && cartLineCurrent(line) > max;
      });
      if (invalidLine) {
        event.preventDefault();
        const line = invalidLine.dataset.cartLine;
        const max = cartLineMax(line);
        changeLine(line, max, {lineMessage: stockLimitMessage(max)});
        return;
      }
    }

    const remove = event.target.closest('[data-cart-remove-line]');
    if (remove) {
      event.preventDefault();
      const line = remove.dataset.cartRemoveLine;
      removeIntent = null;
      clearLineMessage(line);
      changeLine(line, 0, {isRemove: true});
      return;
    }

    const minus = event.target.closest('[data-cart-quantity-minus]');
    if (minus) {
      event.preventDefault();
      const line = minus.dataset.cartQuantityMinus;
      const input = drawer()?.querySelector(`[data-cart-quantity-input="${CSS.escape(line)}"]`);
      const nextQuantity = Math.max(0, Number(input?.value || 1) - 1);
      clearLineMessage(line);
      if (input) input.value = String(nextQuantity);
      changeLine(line, nextQuantity);
      return;
    }

    const plus = event.target.closest('[data-cart-quantity-plus]');
    if (plus) {
      event.preventDefault();
      const line = plus.dataset.cartQuantityPlus;
      const input = drawer()?.querySelector(`[data-cart-quantity-input="${CSS.escape(line)}"]`);
      const currentQuantity = Math.max(0, Math.floor(Number(input?.value || 0)));
      const nextQuantity = currentQuantity + 1;
      const max = cartLineMax(line);
      if (max != null && nextQuantity > max) {
        if (input) input.value = String(Math.min(currentQuantity, max));
        showLineMessage(line, stockLimitMessage(max));
        return;
      }
      clearLineMessage(line);
      if (input) input.value = String(nextQuantity);
      changeLine(line, nextQuantity);
      return;
    }

    const pageMinus = event.target.closest('[data-cart-page-minus]');
    if (pageMinus) {
      event.preventDefault();
      const line = pageMinus.dataset.cartPageMinus;
      const input = cartPageLine(line)?.querySelector(`[data-cart-page-quantity-input="${CSS.escape(String(line))}"]`);
      if (!input) return;
      const max = cartPageLineMax(line);
      const current = Math.max(0, Math.floor(Number(input.value || 0)));
      const next = max != null && current > max ? max : Math.max(0, current - 1);
      input.value = String(next);
      if (max != null && current > max) showCartPageLineMessage(line, stockLimitMessage(max));
      else showCartPageLineMessage(line, '');
      return;
    }

    const pagePlus = event.target.closest('[data-cart-page-plus]');
    if (pagePlus) {
      event.preventDefault();
      const line = pagePlus.dataset.cartPagePlus;
      const input = cartPageLine(line)?.querySelector(`[data-cart-page-quantity-input="${CSS.escape(String(line))}"]`);
      if (!input) return;
      const current = Math.max(0, Math.floor(Number(input.value || 0)));
      const next = current + 1;
      const max = cartPageLineMax(line);
      if (max != null && next > max) {
        input.value = String(max);
        showCartPageLineMessage(line, stockLimitMessage(max));
        return;
      }
      input.value = String(next);
      showCartPageLineMessage(line, '');
      return;
    }
  });

  document.addEventListener('input', (event) => {
    const pageInput = event.target.closest('[data-cart-page-quantity-input]');
    if (pageInput) {
      const line = pageInput.dataset.cartPageQuantityInput;
      const max = cartPageLineMax(line);
      const requested = Math.max(0, Math.floor(Number(pageInput.value || 0)));
      if (max != null && requested > max) {
        pageInput.value = String(max);
        showCartPageLineMessage(line, stockLimitMessage(max));
      } else {
        showCartPageLineMessage(line, '');
      }
      return;
    }

    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    const line = input.dataset.cartQuantityInput;
    const max = cartLineMax(line);
    if (max == null) return;
    const requested = Math.max(0, Math.floor(Number(input.value || 0)));
    if (requested > max) {
      // Do not allow the field itself to sit above the Variant's real stock.
      // Remember that this clamp came from an over-stock attempt so the warning
      // remains visible after the browser fires the subsequent change event.
      input.dataset.cartInventoryClamped = '1';
      input.value = String(max);
      showLineMessage(line, stockLimitMessage(max));
    } else {
      delete input.dataset.cartInventoryClamped;
      clearLineMessage(line);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    const line = input.dataset.cartQuantityInput;

    // If the input blurred because the customer clicked Remove, do not submit
    // the edited quantity first. That old request was the race that caused
    // Remove to be ignored and produced "Unable to update cart" in the footer.
    if (removeIntent) {
      const freshIntent = Date.now() - removeIntent.at < 1500;
      const sameLine = String(removeIntent.line) === String(line);
      if (freshIntent && sameLine) {
        clearLineMessage(line);
        return;
      }
      if (!freshIntent) removeIntent = null;
    }

    const requested = Math.max(0, Math.floor(Number(input.value || 0)));
    const max = cartLineMax(line);

    if (max != null && input.dataset.cartInventoryClamped === '1') {
      delete input.dataset.cartInventoryClamped;
      const warning = stockLimitMessage(max);
      const current = cartLineCurrent(line);
      input.value = String(max);
      if (current === max) {
        showLineMessage(line, warning);
      } else {
        changeLine(line, max, {lineMessage: warning});
      }
      return;
    }

    if (max != null && requested > max) {
      const current = cartLineCurrent(line);
      const warning = stockLimitMessage(max);
      input.value = String(max);
      if (current === max) {
        showLineMessage(line, warning);
      } else {
        changeLine(line, max, {lineMessage: warning});
      }
      return;
    }
    clearLineMessage(line);
    changeLine(line, requested);
  });

  // v2.8.104: isolate the main PDP Add button from app click/submit handlers.
  // The capture listener prevents a second handler from racing another /cart/add
  // request with a stale Variant ID. Option buttons and dynamic checkout are not
  // affected.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-product-form] [data-add-to-cart]');
    if (!button || isEditor()) return;
    const form = button.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addToCart(form, button);
  }, true);

  // Standalone /cart page guard. HTML max validation is also enabled in Liquid,
  // but this capture guard protects checkout/update from programmatic submissions.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-cart-page-form]');
    if (!form) return;
    const invalid = cartPageOverstockLines({useRenderedQuantity: true});
    if (!invalid.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    invalid.forEach(({line, max}) => {
      const input = cartPageLine(line)?.querySelector(`[data-cart-page-quantity-input="${CSS.escape(String(line))}"]`);
      if (input) input.value = String(max);
      showCartPageLineMessage(line, stockLimitMessage(max));
    });
    setCartPageCheckoutDisabled(true);
  }, true);

  // Keyboard/Enter fallback for the same exact product form. Product submissions
  // are captured before app bubble handlers; addon/native non-PDP forms keep the
  // normal generic AJAX path.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form');
    if (!form || isEditor()) return;
    const isAddForm = form.matches('[data-cart-add-form], [data-product-form]') || /\/cart\/add(?:\.js)?(?:\?|$)/.test(form.action || '');
    if (!isAddForm) return;

    if (form.matches('[data-product-form]')) {
      const contactButton = form.querySelector('[data-contact-us]');
      if (contactButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        contactButton.click();
        return;
      }
      if (event.submitter && !event.submitter.matches?.('[data-add-to-cart]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addToCart(form, event.submitter || form.querySelector('[data-add-to-cart]'));
      return;
    }

    if (event.defaultPrevented) return;
    event.preventDefault();
    addToCart(form, event.submitter);
  }, true);

  document.addEventListener('keydown', (event) => {
    const root = drawer();
    if (!root?.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector?.(drawerSelector)) closeDrawer();
    const snapshot = renderedCartSnapshot();
    if (snapshot) {
      updateHeaderCount(snapshot);
      updateFreeShippingProgress(snapshot, event.target || document);
    }
  });

  const init = () => {
    // v2.8.112: remove old replay records. Preserve only a short-lived v112 challenge cooldown so a refresh cannot immediately hammer /cart/add.js again.
    // versions could hard-reload into a Cloudflare verification page; this version
    // never replays or reloads an uncertain add automatically.
    try {
      sessionStorage.removeItem('mufod:pending-cart-add:v109');
      sessionStorage.removeItem('mufod:pending-cart-add:v110');
    } catch (_) {}
    clearExpiredCartChallenge();

    // The cart drawer and header are already rendered by Shopify Liquid with the
    // authoritative cart state. Do not spend a /cart.js request on every page
    // load; that unnecessary read contributed to the 429 threshold seen after a
    // few consecutive adds.
    const snapshot = renderedCartSnapshot();
    if (snapshot) {
      updateHeaderCount(snapshot);
      updateFreeShippingProgress(snapshot, document);
    }
    repairCartPageServerOverstock();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once: true});
  } else {
    init();
  }

})();
